import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, GraduationCap, ImageIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, resolveFileUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { display_name: "", position: "", rank_title: "", photo_file_id: "", bio: "", display_order: 99, is_published: true };

export default function JurulatihAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/trainers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Sila muat naik fail imej sahaja");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload?is_public=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_file_id: res.data.file_id }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat naik gambar");
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      display_name: t.display_name || "",
      position: t.position || "",
      rank_title: t.rank_title || "",
      photo_file_id: t.photo_file_id || "",
      bio: t.bio || "",
      display_order: t.display_order ?? 99,
      is_published: t.is_published ?? true,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/trainers/${editingId}`, form);
        toast.success("Maklumat jurulatih dikemaskini");
      } else {
        await api.post("/trainers", form);
        toast.success("Jurulatih ditambah");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Padam jurulatih "${name}"? Tindakan ini tidak boleh dibatalkan.`)) return;
    await api.delete(`/trainers/${id}`);
    toast.success("Jurulatih dipadam");
    load();
  };

  return (
    <div data-testid="admin-jurulatih-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-piks-black">Jurulatih</h1>
          <p className="text-sm text-piks-muted">Urus barisan jurulatih PPPS yang dipaparkan di laman awam.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-trainer-btn" onClick={openCreate} className="bg-piks-black hover:bg-piks-black/90">
              <Plus className="w-4 h-4 mr-1.5" /> Tambah Jurulatih
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Kemaskini Jurulatih" : "Tambah Jurulatih"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Gambar</Label>
                <input ref={fileInputRef} type="file" accept="image/*" data-testid="trainer-photo-input" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                {form.photo_file_id ? (
                  <div className="relative w-28 h-28 mt-1.5 rounded-lg overflow-hidden border border-piks-border">
                    <img src={resolveFileUrl(`/api/files/${form.photo_file_id}`)} alt="Pratonton" className="w-full h-full object-cover" data-testid="trainer-photo-preview" />
                    <button type="button" data-testid="trainer-photo-remove-btn" onClick={() => setForm((f) => ({ ...f, photo_file_id: "" }))} className="absolute top-1 right-1 p-1 rounded-full bg-piks-black/70 text-white hover:bg-piks-black"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <button type="button" data-testid="trainer-photo-upload-btn" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="w-28 h-28 mt-1.5 rounded-lg border-2 border-dashed border-piks-border flex flex-col items-center justify-center gap-1 text-piks-muted hover:border-piks-gold/50 hover:text-piks-black transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
                    <span className="text-[10px] text-center px-1">{uploading ? "Memuat naik..." : "Muat naik"}</span>
                  </button>
                )}
              </div>
              <div><Label>Nama</Label><Input required data-testid="trainer-name-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Jawatan</Label><Input required data-testid="trainer-position-input" placeholder="Contoh: Ketua Jurulatih" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Pangkat / Kelayakan (pilihan)</Label><Input data-testid="trainer-rank-input" placeholder="Contoh: SARJAN POLIS DIRAJA MALAYSIA (PDRM)" value={form.rank_title} onChange={(e) => setForm({ ...form, rank_title: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>Biografi</Label>
                <p className="text-xs text-piks-muted mt-0.5 mb-1.5">Satu baris = satu perenggan. Mulakan baris dengan "•" untuk senarai bullet.</p>
                <Textarea rows={8} data-testid="trainer-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5" />
              </div>
              <div><Label>Susunan Paparan</Label><Input type="number" data-testid="trainer-order-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 99 })} className="mt-1.5" /></div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-piks-ivory border border-piks-border">
                <Label htmlFor="is_published" className="text-sm">Terbitkan di laman awam</Label>
                <Switch id="is_published" data-testid="trainer-published-switch" checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              </div>
              <Button type="submit" disabled={saving} data-testid="trainer-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{saving ? "Menyimpan..." : "Simpan"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-piks-muted" data-testid="admin-jurulatih-empty-state">
          <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Belum ada jurulatih ditambah.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="p-4 rounded-lg bg-white border border-piks-border flex items-start gap-3" data-testid={`admin-trainer-${t.id}`}>
              {t.photo_file_id ? (
                <img src={resolveFileUrl(`/api/files/${t.photo_file_id}`)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0 border border-piks-border" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-piks-ivory shrink-0 flex items-center justify-center text-piks-black/40 font-display font-bold">{t.display_name?.[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-piks-black truncate">{t.display_name}</p>
                <p className="text-xs text-piks-red font-medium">{t.position}</p>
                {!t.is_published && <p className="text-[10px] text-piks-muted mt-1 uppercase tracking-wide">Tidak diterbitkan</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(t)} data-testid={`edit-trainer-${t.id}`} className="p-1.5 text-piks-muted hover:text-piks-black"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(t.id, t.display_name)} data-testid={`delete-trainer-${t.id}`} className="p-1.5 text-piks-muted hover:text-piks-red"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
